import React, {
  useContext,
  useCallback,
  useState,
  useEffect,
  useMemo,
} from 'react';
import loadable from '@loadable/component';
import classNames from 'classnames';
import styled from 'styled-components';
import Container from 'ls-common-client/dist/components/Container';
import LayoutContainer from '../../LayoutContainer';
import { COOKIES } from '../../../constants';
import { Context } from '../../../context/AppContext';
import useScrollPosition from '../../../hooks/useScrollPosition';
import HeaderBannerChristmas from '../../../assets/header/headerBannerChristmas.png';

const NotificationAlert = loadable(() => import('../../NotificationAlert'), {
  ssr: false,
});

const StyledRoot = styled(Container)`
  &.Header__wrapper--backgroundOverlay .Header__container {
    background-repeat: no-repeat;
    background-size: contain;
    background-position: left center;
  }

  &.Header__StateOfOrigin--QLD .Header__root {
    background-image: linear-gradient(267deg, #8e2752 80%, #620b3a 9%);
  }

  &.Header__StateOfOrigin--NSW .Header__root {
    background-image: linear-gradient(88deg, #1c3567 7%, #1a457d 79%);
  }

  &.Header__StateOfOrigin--NSW .StateOfOrigin--title span {
    color: #54ace4;
    margin-left: 1px;
  }

  &.Header__StateOfOrigin--QLD .StateOfOrigin--title span {
    color: #b89057;
    margin-left: 1px;
  }

  .StateOfOrigin--title {
    display: flex;
    align-items: center;
    margin: 0 20px;
  }

  .StateOfOrigin--title img {
    width: 22px;
  }

  .StateOfOrigin--title p {
    color: #ffffff;
    font-family: 'Circular';
    font-weight: bold;
    font-size: 16px;
    padding: 0;
    margin: 0 0 0 10px;
    text-shadow: 1px 2px rgba(0, 0, 0, 0.5);
  }

  @media (max-width: 767px) {
    &.Header__wrapper--backgroundOverlay .Header__container {
      background-size: cover;
    }

    &.Header__StateOfOrigin--NSW .Header__root {
      background-image: linear-gradient(258deg, #1c3567 81%, #1c3567 9%);
    }

    &.Header__StateOfOrigin--QLD .Header__root {
      background-image: linear-gradient(258deg, #8e2752 81%, #620b3a 9%);
    }
  }
`;

const { NOTIFICATION_ALERT_BANNER } = COOKIES;

const Wrapper = ({
  slideIn,
  className,
  style,
  christmas,
  children,
  headerBottom,
  onHeaderShow = () => {},
  onBannerShow = () => {},
}) => {
  const {
    media: { device },
    cookie: { getCookie },
  } = useContext(Context);
  const [bannerActive, setBannerActive] = useState(
    getCookie(NOTIFICATION_ALERT_BANNER) !== 'false'
  );
  const [showHeader, setShowHeader] = useState(false);
  const [headerRef, setHeaderRef] = useState();
  const [headerHeight, setHeaderHeight] = useState(0);

  const callback = useCallback(() => {
    setShowHeader((slideIn || bannerActive) && window.scrollY > 44);
  }, [slideIn, bannerActive]);

  useScrollPosition(null, callback);

  useEffect(() => {
    const { current = {} } = headerRef || {};
    const timer = setTimeout(() => {
      if (bannerActive) {
        setHeaderHeight(current.offsetHeight);
      } else {
        setHeaderHeight(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [bannerActive, headerRef]);

  useEffect(() => {
    onHeaderShow(showHeader);
    return () => {};
  }, [showHeader]);

  useEffect(() => {
    onBannerShow(bannerActive);
    return () => {};
  }, [bannerActive]);

  const onNotificationChange = useCallback(
    value => {
      const { current = {} } = headerRef || {};
      setBannerActive(value);
      if (value) {
        setHeaderHeight(current.offsetHeight);
      } else {
        setHeaderHeight(false);
      }
    },
    [headerRef]
  );

  const backgroundOverlay = useMemo(() => {
    if (christmas) return HeaderBannerChristmas;
    return null;
  }, [christmas]);

  return (
    <StyledRoot
      style={{
        ...(headerHeight ? { height: headerHeight } : {}),
      }}
      className={classNames('Header__wrapper', {
        [className]: className,
        [`Header__wrapper--${device}`]: device,
        'Header__wrapper--backgroundOverlay': backgroundOverlay,
        'Header__wrapper--notificationAlertHidden': !bannerActive,
        'Header__wrapper--notificationAlertShow': bannerActive,
      })}
    >
      <div
        ref={ref => {
          setHeaderRef(ref);
        }}
        className={classNames('Header__root', {
          'Header__root--slideIn': slideIn,
          'Header__root--show': showHeader,
          'Header__root--fixed': !bannerActive,
          'Header__root--bannerActive': bannerActive,
        })}
      >
        {!showHeader && bannerActive && (
          <NotificationAlert onChange={onNotificationChange} />
        )}
        <LayoutContainer
          className="Header__container"
          type="fullWidth"
          style={{
            ...(style || {}),
            ...{
              ...(backgroundOverlay
                ? {
                    backgroundImage: `url(${backgroundOverlay})`,
                  }
                : {}),
            },
          }}
        >
          {children}
        </LayoutContainer>
        {!!headerBottom && (
          <LayoutContainer className="Header__bottom" type="fullWidth">
            {headerBottom}
          </LayoutContainer>
        )}
      </div>
    </StyledRoot>
  );
};

export default Wrapper;
